<template>
  <div class="blog-page">
    <!-- Breadcrumb -->
    <div
      class="px-5 mt-[76px] w-full md:container md:px-0 md:mt-0 overflow-auto whitespace-nowrap breadcrumbs-container border-manatee border-solid"
    >
      <Breadcrumbs class="" :navigation="navigation" />
    </div>
    <!-- ./Breadcrumb -->

    <!-- Search Article -->
    <SearchBar
      :title="getSearchBarTitle"
      :description="searchResultDescription"
      class="mt-[30px] mb-[40px] md:mb-[66px]"
      :search-value="getSearchQuery"
      @search="onSearch"
    />
    <!-- ./Search Article -->

    <template v-if="!getSearchQuery">
      <!-- Featured Article -->
      <FeaturedArticle
        v-if="postState.featuredArticle && getCurrentPage == 1"
        :featured-article="postState.featuredArticle"
      />
      <!-- ./Featured Article -->

      <!-- Categories -->
      <CategoryList :selected-category="selectedCategory" />
      <!-- ./Categories -->
    </template>

    <!-- Post List -->
    <PostList :posts="postState.list.posts" />
    <!-- ./Post List -->

    <!-- Pagination -->
    <Pagination
      v-if="postState.list.totalPage > 1"
      :current-page="getCurrentPage"
      :total-page="postState.list.totalPage"
      :route-path="getRoutePath"
    />
    <!-- ./Pagination -->

    <!-- Email Subscription -->
    <div class="email-subscription">
      <div class="email-subscription__container">
        <EmailSubscription :no-wrap="true" />
      </div>
    </div>
    <!-- ./Email Subscription -->
  </div>
</template>
<script>
import Breadcrumbs from '~/components/merchant/Breadcrumbs.vue';
import SearchBar from '~/components/blog/SearchBar.vue';
import FeaturedArticle from '~/components/blog/FeaturedArticle.vue';
import CategoryList from '~/components/blog/categories/CategoryList.vue';
import PostList from '~/components/blog/PostList.vue';
import EmailSubscription from '~/components/blog/subscriptionBar/EmailSubscription.vue';

const Pagination = () => import('~/components/blog/Pagination.vue');

export default {
  name: 'Blog',
  components: {
    Breadcrumbs,
    SearchBar,
    FeaturedArticle,
    CategoryList,
    PostList,
    EmailSubscription,
    Pagination,
  },
  head() {
    const { description } = this.selectedCategory;
    const title = this.selectedCategory.name;
    const link = this.getHeadLinks;

    return {
      title,
      htmlAttrs: {
        lang: this.getHrefLang,
      },
      meta: [
        { 'http-equiv': 'content-language', content: this.getHrefLang },
        {
          property: 'og:title',
          content: title,
        },
        {
          name: 'description',
          content: description,
        },
        {
          property: 'og:description',
          content: description,
        },
        { name: 'twitter:card', content: 'summary' },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:site',
          content: this.language.socials.twitter ? `@${this.language.socials.twitter.split('/').pop()}` : '',
        },
        {
          name: 'twitter:creator',
          content: this.language.socials.twitter ? `@${this.language.socials.twitter.split('/').pop()}` : '',
        },
        {
          name: 'twitter:description',
          content: description,
        },
        { property: 'og:type', content: 'article' },
        { property: 'og:locale', content: this.getHrefLang },
        {
          property: 'og:url',
          content: `${this.$paths.url(false)}${
            this.$route.path[this.$route.path.length - 1] === '/'
              ? `${this.$route.path.substring(0, this.$route.path.length - 1)}/`
              : `${this.$route.path}/`
          }`,
        },
      ],
      link,
    };
  },
  computed: {
    selectedCategory() {
      return this.$store.state.category.selectedCategory;
    },
    navigation() {
      return [
        {
          href: '/',
          title: 'Home',
        },
        {
          href: '/blog/',
          title: 'Blog',
        },
        {
          href: `/blog/${this.selectedCategory.slug}/`,
          title: this.selectedCategory.name,
        },
      ];
    },
    language() {
      return this.$store.state.language;
    },
    postState() {
      return this.$store.state.post;
    },
    getCurrentPage() {
      return parseInt(this.$route?.query?.page || 1, 10);
    },
    getRoutePath() {
      return this.$router.resolve({
        name: 'blog-value',
        params: {
          value: this.selectedCategory.slug,
        },
      }).href;
    },
    languages() {
      return this.$store.state.languages;
    },
    getHeadLinks() {
      const links = [];
      const { slug } = this.selectedCategory;

      let canonical = this.$paths.url(false);

      if (this.getCurrentPage > 1) {
        canonical += this.$router.resolve({
          name: 'blog-value',
          params: {
            value: slug,
          },
        }).href;
        canonical += `/?page=${this.getCurrentPage}`;
      } else {
        canonical += this.$route.path + (this.$route.path.slice(-1) !== '/' ? '/' : '');
      }

      links.push({
        rel: 'canonical',
        href: canonical,
      });

      const previousPage = this.getCurrentPage - 1;
      if (previousPage > 0) {
        const prevPageResolver = {
          name: 'blog-value',
          params: {
            value: slug,
          },
        };

        let prevPageLink = `${this.$router.resolve(prevPageResolver).href}/`;

        if (previousPage !== 1) {
          prevPageLink += `?page=${previousPage}`;
        }

        links.push({
          rel: 'prev',
          href: this.$paths.url(false) + prevPageLink,
        });
      }

      const nextPage = this.getCurrentPage + 1;
      if (nextPage <= this.postState.list.totalPage) {
        let nextPageLink = this.$router.resolve({
          name: 'blog-value',
          params: {
            value: slug,
          },
        }).href;
        nextPageLink += `/?page=${nextPage}`;

        links.push({
          rel: 'next',
          href: this.$paths.url(false) + nextPageLink,
        });
      }
      const category = this.categoryList.find((cl) => cl.id === this.selectedCategory.id);
      const alternates = category?.hreflangs
        // eslint-disable-next-line arrow-body-style
        .filter((m) => {
          // Only include hreflangs that have a corresponding language
          return this.languages.some((l) => l.lang === m.lang);
        })
        // eslint-disable-next-line array-callback-return, consistent-return
        .map((m) => {
          const language = this.languages.find((l) => l.lang === m.lang);
          if (language) {
            return {
              rel: 'alternate',
              hreflang: m.lang === 'en-eu' ? 'en' : m.lang,
              href: `https://${language.domain}/blog/${m.slug}/`,
            };
          }
        });
      links.push(...alternates);

      return links;
    },
    categoryList() {
      return this.$store.state.category.categories;
    },
    getSearchQuery() {
      return this.$route?.query?.q;
    },
    searchResultDescription() {
      let message = this.selectedCategory?.description;

      if (this.getSearchQuery) {
        const postCount = this.postState.list.posts.length;

        if (postCount) {
          message = this.$i18n('blog.posts-found', { count: postCount });
        } else {
          message = this.$i18n('blog.no-result-for-search');
        }
      }

      return message;
    },
    getSearchBarTitle() {
      return this.getSearchQuery
        ? this.$i18n('blog.search-results-for', {
            category: this.getSearchQuery,
          })
        : this.selectedCategory?.name;
    },
    getHrefLang() {
      return this.language.lang === 'en-eu' ? 'en' : this.language.lang;
    },
  },
  methods: {
    onSearch(text) {
      let categorySearchPath = this.$router.resolve({
        name: 'blog-value',
        params: {
          value: this.selectedCategory?.slug,
        },
      }).href;

      if (text && text.length) {
        categorySearchPath += `/?q=${text}`;
      }

      window.location = categorySearchPath;
    },
  },
  jsonld() {
    const title = this.selectedCategory.name;

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': `${this.$paths.url(false)}/blog/`,
            name: 'Blog',
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@id': `${this.$paths.url(false) + this.$route.path}`,
            name: title ?? '',
          },
        },
      ],
    };
  },
};
</script>

<style lang="scss">
.email-subscription {
  @apply flex flex-row bg-[#f4f7fe];

  &__container {
    @apply px-5 lg:px-0 md:container py-[60px] content-center items-center;
  }
}
.article-list {
  @apply md:container px-5 lg:px-0 md:px-0 mb-[30px] grid grid-cols-6 gap-x-[30px];

  .article-card {
    @apply lg:col-span-2 col-span-full;

    &:nth-child(1),
    &:nth-child(2) {
      @apply border-none;
    }
  }
}
</style>
